import { useState, useEffect, useRef } from "react";
import { CLIENT_COLORS } from "../constants";
import { isoToday, curMonth, fmtDate } from "../utils";
import { Dot, Btn, Modal, Field, Inp, Sel, Txta, Empty, Card, FormRow, Stage } from "../components/primitives";

// ── Helpers ───────────────────────────────────────────────────────────────────
function getWorkDaysFrom(fromDate, count) {
  const days = [];
  const d = new Date(fromDate + "T12:00:00");
  let guard = 0;
  while (days.length < count && guard++ < 200) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days.push(d.toISOString().split("T")[0]);
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function calcPriority(client, planos, anotacoes, agenda, today, month) {
  let u = 0;
  const planoMes = planos.find((p) => p.client_id === client.id && p.month === month);
  if (!planoMes) { u += 30; }
  else {
    const pct = planoMes.items.length
      ? Math.round((planoMes.items.filter((i) => i.done).length / planoMes.items.length) * 100) : 0;
    u += Math.max(0, 50 - pct);
  }
  const lastSession = [...agenda]
    .filter((d) => d.client_id === client.id && d.concluido)
    .sort((a, b) => b.date.localeCompare(a.date))[0];
  const daysSince = lastSession
    ? Math.floor((new Date(today + "T12:00:00") - new Date(lastSession.date + "T12:00:00")) / 86400000) : 30;
  u += Math.min(daysSince * 2, 40);
  if (client.dia_relatorio) {
    const diff = client.dia_relatorio - new Date().getDate();
    if (diff >= 0 && diff <= 3) u += 25;
  }
  const lastAno = [...anotacoes]
    .filter((a) => a.client_id === client.id)
    .sort((a, b) => b.data.localeCompare(a.data))[0];
  if (!lastAno || Math.floor((new Date(today + "T12:00:00") - new Date(lastAno.data + "T12:00:00")) / 86400000) > 14) u += 15;
  return u;
}

function generateSchedule(activeClients, existingAgenda, planos, anotacoes, today, month) {
  const scheduledDates = new Set(existingAgenda.filter((d) => !d.urgente).map((d) => d.date));
  const workDays = getWorkDaysFrom(today, 40).filter((d) => !scheduledDates.has(d));
  if (!workDays.length || !activeClients.length) return [];
  const scored = [...activeClients]
    .map((c) => ({ client: c, priority: calcPriority(c, planos, anotacoes, existingAgenda, today, month) }))
    .sort((a, b) => b.priority - a.priority);
  return workDays.map((date, i) => ({
    date, client_id: scored[i % scored.length].client.id, urgente: false, concluido: false,
  }));
}

// ── DisciplineBar ─────────────────────────────────────────────────────────────
function DisciplineBar({ agenda, today }) {
  const days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today + "T12:00:00");
    d.setDate(d.getDate() - (29 - i));
    return d.toISOString().split("T")[0];
  });

  const workDays     = days.filter((d) => ![0, 6].includes(new Date(d + "T12:00:00").getDay()) && d <= today);
  const scheduled    = workDays.filter((d) => agenda.some((a) => a.date === d && !a.urgente));
  const done         = scheduled.filter((d) => agenda.some((a) => a.date === d && a.concluido));
  const pct          = scheduled.length > 0 ? Math.round((done.length / scheduled.length) * 100) : 0;

  let streak = 0;
  for (let i = 1; i <= 30; i++) {
    const d = new Date(today + "T12:00:00");
    d.setDate(d.getDate() - i);
    const ds = d.toISOString().split("T")[0];
    if ([0, 6].includes(d.getDay())) continue;
    const entry = agenda.find((a) => a.date === ds && !a.urgente);
    if (entry?.concluido) { streak++; }
    else if (entry) break;
    else break;
  }

  const pctColor = pct >= 70 ? "var(--success-ink)" : pct >= 40 ? "var(--warn-ink)" : "var(--danger-ink)";

  return (
    <div style={{ padding: "14px 18px", background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 12, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>Disciplina — últimos 30 dias</span>
        <div style={{ display: "flex", gap: 16 }}>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>
            Streak: <strong style={{ color: streak > 0 ? "var(--success-ink)" : "var(--faint)" }}>{streak} {streak === 1 ? "dia" : "dias"}</strong>
          </span>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>
            Taxa: <strong style={{ color: pctColor }}>{pct}%</strong>
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {days.map((ds) => {
          const dow  = new Date(ds + "T12:00:00").getDay();
          const isWe = dow === 0 || dow === 6;
          const isFu = ds > today;
          const isTo = ds === today;
          const entry = agenda.find((a) => a.date === ds && !a.urgente);
          let bg = "var(--line-soft)";
          if (!isWe && !isFu) {
            if (entry?.concluido) bg = "var(--success)";
            else if (entry)        bg = "var(--danger-soft)";
          }
          if (isTo) bg = entry?.concluido ? "var(--success)" : "var(--primary)";
          return (
            <div key={ds} title={`${ds}${entry?.concluido ? " ✓" : entry ? " ✗" : ""}`}
              style={{ width: 18, height: 18, borderRadius: 4, background: bg, opacity: isWe ? 0.25 : 1,
                outline: isTo ? "2px solid var(--primary)" : "none", outlineOffset: 1, flexShrink: 0 }} />
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
        {[["var(--success)", "Concluído"], ["var(--danger-soft)", "Não concluído"], ["var(--primary)", "Hoje"], ["var(--line-soft)", "Sem agenda / fim de semana"]].map(([c, l]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: c, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: "var(--faint)" }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── buildPriorityList — monta checklist real a partir dos dados do cliente ──────
function buildPriorityList(client, tasks, planos, anotacoes, month, today) {
  const items = [];
  const lvlOrder = { danger: 0, warn: 1, info: 2, success: 3 };

  const planoMes   = planos.find((p) => p.client_id === client?.id && p.month === month);
  const planoTotal = planoMes?.items?.length || 0;
  const planoDone  = planoMes?.items?.filter((i) => i.done).length || 0;
  const planoPct   = planoTotal ? Math.round((planoDone / planoTotal) * 100) : null;

  const acTask  = tasks.find((t) => t.client_id === client?.id && t.type === "AC" && t.month === month && !t.archived);
  const acTotal = acTask ? (acTask.checklist || []).length : 0;
  const acDone  = acTask ? (acTask.checklist || []).filter((x) => x.done).length : 0;

  const lastAno = [...anotacoes]
    .filter((a) => a.client_id === client?.id)
    .sort((a, b) => b.data.localeCompare(a.data))[0];
  const daysSinceAno = lastAno
    ? Math.floor((new Date(today + "T12:00:00") - new Date(lastAno.data + "T12:00:00")) / 86400000) : null;

  // 1. Meta-tasks (não checkáveis — redirecionam para o perfil)
  if (daysSinceAno === null)
    items.push({ id: "meta-ano", icon: "📝", text: "Criar primeiro registro / anotação do cliente", lvl: "danger", type: "navigate" });
  else if (daysSinceAno > 30)
    items.push({ id: "meta-ano", icon: "📝", text: `Última anotação há ${daysSinceAno} dias — registrar reunião urgente`, lvl: "danger", type: "navigate" });
  else if (daysSinceAno > 14)
    items.push({ id: "meta-ano", icon: "📝", text: `Última anotação há ${daysSinceAno} dias — registrar update`, lvl: "warn", type: "navigate" });

  if (!planoMes)
    items.push({ id: "meta-plano", icon: "📋", text: "Plano mensal não criado — criar agora", lvl: "danger", type: "navigate" });

  if (client?.dia_relatorio) {
    const diff = client.dia_relatorio - new Date().getDate();
    if (diff === 0)      items.push({ id: "meta-rel", icon: "📊", text: "Relatório vence hoje — enviar agora!", lvl: "danger", type: "navigate" });
    else if (diff === 1) items.push({ id: "meta-rel", icon: "📊", text: "Relatório vence amanhã — preparar hoje", lvl: "danger", type: "navigate" });
    else if (diff <= 3)  items.push({ id: "meta-rel", icon: "📊", text: `Relatório em ${diff} dias (dia ${client.dia_relatorio})`, lvl: "warn", type: "navigate" });
  }

  // 2. Itens do Plano mensal (checkáveis)
  if (planoMes) {
    const planoLvl = planoPct === null || planoPct < 30 ? "danger" : planoPct < 70 ? "warn" : "info";
    (planoMes.items || [])
      .filter((item) => !item.done)
      .forEach((item, idx) => {
        const label = item.text || item.label || item.nome || `Item ${idx + 1}`;
        const group = item.group ? `[${item.group}] ` : "";
        items.push({
          id: `plano-${item.id ?? idx}`,
          icon: "📋",
          text: `${group}${label}`,
          lvl: planoLvl,
          type: "plano",
          planoId: planoMes.id,
          itemKey: item.id ?? idx,
          allItems: planoMes.items,
        });
      });
  }

  // 3. Itens do AC semanal (checkáveis)
  if (acTask) {
    const acLvl = acDone === 0 ? "warn" : "info";
    (acTask.checklist || [])
      .filter((item) => !item.done)
      .forEach((item, idx) => {
        const group = item.group ? `[${item.group}] ` : "";
        items.push({
          id: `ac-${item.id ?? idx}`,
          icon: "✅",
          text: `${group}${item.text}`,
          lvl: acLvl,
          type: "ac",
          taskId: acTask.id,
          itemKey: item.id ?? idx,
          allChecklist: acTask.checklist,
        });
      });
  }

  // Ordena por urgência
  items.sort((a, b) => lvlOrder[a.lvl] - lvlOrder[b.lvl]);

  return { items, planoPct, planoTotal, planoDone, acTotal, acDone, daysSinceAno };
}

// ── TodayHero ─────────────────────────────────────────────────────────────────
function TodayHero({ dia, client, tasks, planos, anotacoes, month, today, onUpdate, onGoClient, onUpdateTask, onUpdatePlano }) {
  const [saving,   setSaving]   = useState(false);
  const [checking, setChecking] = useState(null);
  const [showAll,  setShowAll]  = useState(false);

  const col      = CLIENT_COLORS[client?.type] || CLIENT_COLORS["Interno"];
  const initials = client?.name?.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  const { items, planoPct, planoTotal, planoDone, acTotal, acDone, daysSinceAno } =
    buildPriorityList(client, tasks, planos, anotacoes, month, today);

  const barColor = planoPct === null ? "var(--line-soft)" : planoPct === 100 ? "var(--success)" : planoPct >= 60 ? "var(--primary)" : planoPct >= 30 ? "var(--warn)" : "var(--danger)";

  const SHOW = 8;
  const visible   = showAll ? items : items.slice(0, SHOW);
  const remaining = items.length - SHOW;

  const lvlDot = { danger: "var(--danger)", warn: "var(--warn)", info: "var(--primary)", success: "var(--success)" };
  const lvlBg  = { danger: "var(--danger-soft)", warn: "var(--warn-soft)", info: "var(--primary-soft)", success: "var(--success-soft)" };

  async function handleCheck(item) {
    if (item.type === "navigate") { onGoClient(client.id); return; }
    if (checking) return;
    setChecking(item.id);
    try {
      if (item.type === "plano" && onUpdatePlano) {
        const newItems = item.allItems.map((i, idx) =>
          (i.id ?? idx) === item.itemKey ? { ...i, done: true } : i
        );
        await onUpdatePlano(item.planoId, { items: newItems });
      } else if (item.type === "ac" && onUpdateTask) {
        const newChecklist = item.allChecklist.map((i, idx) =>
          (i.id ?? idx) === item.itemKey ? { ...i, done: true } : i
        );
        await onUpdateTask(item.taskId, { checklist: newChecklist });
      }
    } finally { setChecking(null); }
  }

  return (
    <div style={{ border: "1px solid var(--line-soft)", borderRadius: 14, overflow: "hidden", marginBottom: 20, background: "var(--surface)" }}>
      {/* Header strip */}
      <div style={{ padding: "10px 20px", background: dia.concluido ? "var(--success-soft)" : "var(--primary-soft)", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: dia.concluido ? "var(--success-ink)" : "var(--primary)", textTransform: "uppercase", letterSpacing: ".07em" }}>
          {dia.urgente ? "⚡ Urgência do dia" : "Foco de hoje"}
        </span>
        <div style={{ flex: 1 }} />
        {dia.concluido && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--success-ink)" }}>✓ Dia concluído</span>}
      </div>

      <div style={{ padding: "18px 22px" }}>
        {/* Client info row */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: col.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: col.tx }}>{initials}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)" }}>{client.name}</div>
            <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
              {planoPct !== null && <span style={{ fontSize: 11, color: "var(--muted)" }}>Plano <strong style={{ color: barColor }}>{planoPct}%</strong> · {planoDone}/{planoTotal}</span>}
              {acTotal > 0 && <span style={{ fontSize: 11, color: "var(--muted)" }}>AC <strong style={{ color: acDone === acTotal ? "var(--success-ink)" : "var(--ink)" }}>{acDone}/{acTotal}</strong></span>}
              {daysSinceAno !== null && <span style={{ fontSize: 11, color: "var(--muted)" }}>Nota <strong style={{ color: daysSinceAno > 14 ? "var(--warn-ink)" : "var(--ink)" }}>{daysSinceAno}d</strong></span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {!dia.concluido && onUpdate && (
              <Btn variant="accent" sm onClick={async () => { setSaving(true); await onUpdate(dia.id, { concluido: true }); setSaving(false); }} disabled={saving}>
                {saving ? "…" : "✓ Concluir dia"}
              </Btn>
            )}
            <Btn variant="secondary" sm onClick={() => onGoClient(client.id)}>Abrir perfil →</Btn>
          </div>
        </div>

        {/* Plano progress bar */}
        {planoPct !== null && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ height: 4, background: "var(--line-soft)", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${planoPct}%`, background: barColor, borderRadius: 99, transition: "width .3s" }} />
            </div>
          </div>
        )}

        {/* Priority checklist */}
        {items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "16px 0", fontSize: 13, color: "var(--success-ink)", fontWeight: 600 }}>
            🎯 Tudo em ordem com este cliente — dia concluído com excelência!
          </div>
        ) : (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
              Prioridades do dia — {items.length} item{items.length !== 1 ? "s" : ""} restante{items.length !== 1 ? "s" : ""}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {visible.map((item) => {
                const isLoading = checking === item.id;
                const isNav = item.type === "navigate";
                return (
                  <div
                    key={item.id}
                    onClick={() => !checking && handleCheck(item)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                      borderRadius: 8, cursor: checking ? "wait" : "pointer",
                      background: isLoading ? "var(--bg)" : "var(--bg)",
                      border: `1px solid var(--line-soft)`,
                      transition: "background .15s",
                      opacity: isLoading ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = lvlBg[item.lvl]; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "var(--bg)"; }}
                  >
                    {/* Checkbox ou seta */}
                    {isNav ? (
                      <div style={{ width: 18, height: 18, borderRadius: 4, background: lvlBg[item.lvl], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 10, color: lvlDot[item.lvl] }}>→</span>
                      </div>
                    ) : (
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", border: `2px solid ${lvlDot[item.lvl]}`,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        background: isLoading ? lvlDot[item.lvl] : "transparent",
                      }}>
                        {isLoading && <span style={{ fontSize: 8, color: "#fff" }}>✓</span>}
                      </div>
                    )}
                    {/* Dot de prioridade */}
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: lvlDot[item.lvl], flexShrink: 0 }} />
                    {/* Texto */}
                    <span style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.4, flex: 1 }}>{item.text}</span>
                    {/* Badge de origem */}
                    <span style={{ fontSize: 9, color: "var(--faint)", flexShrink: 0, textTransform: "uppercase" }}>
                      {item.type === "plano" ? "Plano" : item.type === "ac" ? "AC" : "↗"}
                    </span>
                  </div>
                );
              })}
            </div>
            {!showAll && remaining > 0 && (
              <div
                onClick={() => setShowAll(true)}
                style={{ marginTop: 8, fontSize: 12, color: "var(--primary)", cursor: "pointer", textAlign: "center", padding: "6px 0" }}
              >
                + {remaining} item{remaining !== 1 ? "s" : ""} restante{remaining !== 1 ? "s" : ""} — ver todos
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── WeekendCard ───────────────────────────────────────────────────────────────
function WeekendCard({ todayDow, nextScheduledDia, clientMap, tasks, planos, anotacoes, month, today, onGoClient }) {
  const dayName = todayDow === 6 ? "Sábado" : "Domingo";
  const next = nextScheduledDia && clientMap[nextScheduledDia?.client_id];

  const { items } = next
    ? buildPriorityList(next, tasks, planos, anotacoes, month, today)
    : { items: [] };

  // Só sugestões danger/warn para adiantar, máx 3
  const sugestoes = items.filter((a) => a.lvl === "danger" || a.lvl === "warn").slice(0, 3);

  const nextDateStr = nextScheduledDia
    ? new Date(nextScheduledDia.date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" })
    : null;

  return (
    <div style={{ border: "1px solid var(--line-soft)", borderRadius: 14, overflow: "hidden", marginBottom: 20, background: "var(--surface)" }}>
      <div style={{ padding: "10px 20px", background: "var(--primary-soft)" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--primary)", textTransform: "uppercase", letterSpacing: ".07em" }}>
          {dayName} — dia de descanso
        </span>
      </div>
      <div style={{ padding: "18px 22px" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>
          Hoje não há agenda de trabalho. Curte a família! 🏡
        </div>
        {next && nextDateStr && (
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: sugestoes.length > 0 ? 16 : 0 }}>
            Próximo foco:{" "}
            <strong style={{ color: "var(--ink)", textTransform: "capitalize" }}>{nextDateStr}</strong>
            {" — "}
            <span
              style={{ color: "var(--primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => onGoClient(next.id)}
            >
              {next.name}
            </span>
          </div>
        )}

        {sugestoes.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
              Se quiser adiantar — vai sair na frente 💪
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(sugestoes.length, 3)}, 1fr)`, gap: 8 }}>
              {sugestoes.map((s, i) => (
                <div key={i} style={{
                  background: "var(--bg)", border: "1px dashed var(--line-soft)", borderRadius: 8,
                  padding: "10px 12px", display: "flex", alignItems: "flex-start", gap: 8,
                }}>
                  <span style={{ flexShrink: 0 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>
                      {next.name.length > 20 ? next.name.slice(0, 20) + "…" : next.name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink)", lineHeight: 1.45 }}>{s.text}</div>
                  </div>
                </div>
              ))}
            </div>
            {next && (
              <div style={{ marginTop: 12 }}>
                <Btn variant="ghost" sm onClick={() => onGoClient(next.id)}>
                  Ver perfil de {next.name.split(" ").slice(0, 2).join(" ")} →
                </Btn>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── DayChip ───────────────────────────────────────────────────────────────────
function DayChip({ dateStr, dia, client, today, onClick }) {
  const col    = client ? CLIENT_COLORS[client.type] || CLIENT_COLORS["Interno"] : null;
  const isTo   = dateStr === today;
  const isPast = dateStr < today;
  const d      = new Date(dateStr + "T12:00:00");
  const dayLbl = d.toLocaleDateString("pt-BR", { weekday: "short" });
  const dateLbl= d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

  return (
    <div onClick={onClick} style={{
      padding: "10px 13px", borderRadius: 10, cursor: "pointer", minWidth: 108, flexShrink: 0,
      background: isTo ? "var(--primary-soft)" : isPast && dia?.concluido ? "var(--success-soft)" : "var(--surface)",
      border: `1px solid ${isTo ? "var(--primary)" : isPast && dia?.concluido ? "var(--success)" : "var(--line-soft)"}`,
      opacity: isPast && !dia?.concluido ? 0.65 : 1,
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: isTo ? "var(--primary)" : "var(--faint)", textTransform: "uppercase", marginBottom: 5 }}>
        {dayLbl} {dateLbl}
      </div>
      {client ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
            {col && <Dot color={col.dot} size={5} />}
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ink)", lineHeight: 1.3 }}>
              {client.name.length > 16 ? client.name.slice(0, 16) + "…" : client.name}
            </span>
          </div>
          {dia?.urgente && <span style={{ fontSize: 9, color: "var(--warn-ink)", fontWeight: 700 }}>⚡ Urgente</span>}
          {dia?.concluido && <span style={{ fontSize: 9, color: "var(--success-ink)", fontWeight: 700 }}>✓ Feito</span>}
        </>
      ) : (
        <div style={{ fontSize: 11, color: "var(--faint)" }}>Livre</div>
      )}
    </div>
  );
}

// ── EventModal (mantido) ──────────────────────────────────────────────────────
function EventModal({ initial, clients, onClose, onSave, onDelete }) {
  const [f, setF] = useState(initial || {
    title: "", description: "", event_date: isoToday(), event_time: "",
    type: "Reunião", color: "var(--primary)", client_id: "",
  });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);
  async function save() { setSaving(true); await onSave(f); setSaving(false); }
  return (
    <Modal title={initial ? "Editar evento" : "Novo evento"} onClose={onClose} width={460}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Título"><Inp value={f.title} onChange={(e) => up("title", e.target.value)} /></Field>
        <FormRow>
          <Field label="Data" half><Inp type="date" value={f.event_date} onChange={(e) => up("event_date", e.target.value)} /></Field>
          <Field label="Horário" half><Inp type="time" value={f.event_time || ""} onChange={(e) => up("event_time", e.target.value)} /></Field>
        </FormRow>
        <FormRow>
          <Field label="Tipo" half>
            <Sel value={f.type || "Reunião"} onChange={(e) => up("type", e.target.value)}>
              {["Reunião","AC","AT","Apresentação","Prazo","Outro"].map((t) => <option key={t}>{t}</option>)}
            </Sel>
          </Field>
          <Field label="Cliente" half>
            <Sel value={f.client_id || ""} onChange={(e) => up("client_id", e.target.value || null)}>
              <option value="">— Sem cliente —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <Field label="Descrição"><Txta rows={2} value={f.description || ""} onChange={(e) => up("description", e.target.value)} /></Field>
        <div style={{ display: "flex", gap: 8 }}>
          {onDelete && <Btn variant="danger" sm onClick={() => { if (window.confirm("Excluir evento?")) onDelete(); }}>Excluir</Btn>}
          <Btn variant="accent" full onClick={save} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── UrgenciaModal ─────────────────────────────────────────────────────────────
function UrgenciaModal({ clients, today, onClose, onSave }) {
  const [clientId, setClientId] = useState("");
  const [saving, setSaving]     = useState(false);
  async function save() {
    if (!clientId) return;
    setSaving(true);
    await onSave({ date: today, client_id: clientId, urgente: true, concluido: false });
    setSaving(false);
  }
  return (
    <Modal title="⚡ Inserir demanda urgente" subtitle="Adiciona um cliente extra ao dia de hoje" onClose={onClose} width={400}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Cliente com demanda urgente">
          <Sel value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">— Selecione —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Sel>
        </Field>
        <Btn variant="accent" full onClick={save} disabled={saving || !clientId}>
          {saving ? "Salvando…" : "⚡ Adicionar urgência hoje"}
        </Btn>
      </div>
    </Modal>
  );
}

// ── AgendaView ────────────────────────────────────────────────────────────────
export function AgendaView({
  events, clients, tasks, planos = [], historico = [], anotacoes = [],
  // `agendaCarregada` default FALSE de proposito: quem esquecer de passar a
  // prop perde a auto-geracao, e nao ganha uma auto-geracao cega.
  agenda = [], agendaCarregada = false, onNew, onUpdate, onDelete,
  onNewAgenda, onUpdateAgenda, onNewAgendaBatch,
  onGoClient, onUpdateTask, onUpdatePlano,
}) {
  const today  = isoToday();
  const month  = curMonth();
  const [showEventForm,    setShowEventForm]    = useState(false);
  const [selectedEvent,    setSelectedEvent]    = useState(null);
  const [showUrgencia,     setShowUrgencia]     = useState(false);
  const [generating,       setGenerating]       = useState(false);
  const autoGenDone = useRef(false);

  const activeClients = clients.filter((c) => c.status === "Ativo" && c.type !== "Interno");
  const clientMap     = clients.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  // ── Agenda entries ────────────────────────────────────────────────────────
  const todayDias   = agenda.filter((d) => d.date === today);
  const todayMain   = todayDias.find((d) => !d.urgente);
  const todayUrgent = todayDias.find((d) => d.urgente);

  const todayDow    = new Date(today + "T12:00:00").getDay();
  const isWeekend   = todayDow === 0 || todayDow === 6;

  // Próximos 14 dias úteis (excluindo hoje)
  const nextWorkDays = getWorkDaysFrom(today, 15).slice(1, 15);

  // Agenda já existe para os próximos dias?
  const hasSchedule = agenda.some((d) => d.date >= today && !d.urgente);

  // Próximo dia útil com cliente agendado
  const nextScheduledDia = [...agenda]
    .filter((d) => d.date > today && !d.urgente)
    .sort((a, b) => a.date.localeCompare(b.date))[0];

  async function handleGenerate() {
    // Trava 1: gerar a partir de uma leitura que falhou cria um mês inteiro
    // por cima do que já existe. Sem confirmação de leitura, não gera.
    if (!agendaCarregada) return;

    setGenerating(true);
    try {
      const newEntries = generateSchedule(activeClients, agenda, planos, anotacoes, today, month);

      // Trava 2: nenhuma data que já tenha rodízio pode ser reagendada.
      // `generateSchedule` já filtra, mas ele filtra contra o `agenda` que
      // recebeu — e é justamente esse array que pode estar desatualizado.
      // Repetir a verificação aqui custa nada e fecha a janela.
      const jaAgendadas = new Set(agenda.filter((d) => !d.urgente).map((d) => d.date));
      const semDuplicar = newEntries.filter((e) => !jaAgendadas.has(e.date));

      if (semDuplicar.length > 0) await onNewAgendaBatch(semDuplicar);
    } finally { setGenerating(false); }
  }

  // Auto-gera na primeira abertura APENAS quando a leitura da agenda
  // confirmadamente deu certo e voltou vazia. Em 05/09/2026 esta condição
  // não existia: um `.catch(() => [])` no App fazia falha de leitura passar
  // por agenda vazia, e a auto-geração criou 40 dias sobre os 40 que já
  // estavam lá — o mesmo rodízio deslocado em um dia útil.
  useEffect(() => {
    if (!agendaCarregada) return;
    if (!autoGenDone.current && !hasSchedule && activeClients.length > 0 && onNewAgendaBatch) {
      autoGenDone.current = true;
      handleGenerate();
    }
  }, [agendaCarregada, hasSchedule, activeClients.length]); // eslint-disable-line

  // ── Events ────────────────────────────────────────────────────────────────
  const todayEvents = events.filter((e) => e.event_date === today);
  const upcomingEvents = [...events]
    .filter((e) => e.event_date > today)
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .slice(0, 6);

  const nowStr = new Date().toLocaleDateString("pt-BR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="view">

      {/* ── Palco ──────────────────────────────────────────────────────────── */}
      <Stage
        path="agenda"
        title="Agenda do"
        hl="mês"
        lead={nowStr.charAt(0).toUpperCase() + nowStr.slice(1)}
        actions={
          <>
            <Btn sm variant="secondary" onClick={() => setShowUrgencia(true)}>⚡ Urgência hoje</Btn>
            {!hasSchedule && (
              <Btn sm variant="primary" onClick={handleGenerate} disabled={generating}>
                {generating ? "Gerando…" : "✦ Gerar agenda do mês"}
              </Btn>
            )}
            <Btn sm variant="secondary" onClick={() => setShowEventForm(true)}>+ Evento</Btn>
          </>
        }
      />

      {/* ── Disciplina ─────────────────────────────────────────────────────── */}
      <DisciplineBar agenda={agenda} today={today} />

      {/* ── Hoje — hero ────────────────────────────────────────────────────── */}
      {todayMain && clientMap[todayMain.client_id] ? (
        <TodayHero
          dia={todayMain}
          client={clientMap[todayMain.client_id]}
          tasks={tasks} planos={planos} anotacoes={anotacoes}
          month={month} today={today}
          onUpdate={onUpdateAgenda}
          onGoClient={onGoClient}
          onUpdateTask={onUpdateTask}
          onUpdatePlano={onUpdatePlano}
        />
      ) : isWeekend ? (
        <WeekendCard
          todayDow={todayDow}
          nextScheduledDia={nextScheduledDia}
          clientMap={clientMap}
          tasks={tasks} planos={planos} anotacoes={anotacoes}
          month={month} today={today}
          onGoClient={onGoClient}
        />
      ) : (
        /* Dia útil sem agenda — precisa gerar */
        <Card style={{ padding: "28px 24px", marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📅</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>
            Nenhum cliente agendado para hoje
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
            Clique em "Gerar agenda do mês" para o sistema distribuir os clientes automaticamente por prioridade.
          </div>
          {!hasSchedule && (
            <Btn variant="accent" onClick={handleGenerate} disabled={generating}>
              {generating ? "Gerando…" : "✦ Gerar agenda do mês"}
            </Btn>
          )}
        </Card>
      )}

      {/* Urgência de hoje */}
      {todayUrgent && clientMap[todayUrgent.client_id] && (
        <TodayHero
          dia={todayUrgent}
          client={clientMap[todayUrgent.client_id]}
          tasks={tasks} planos={planos} anotacoes={anotacoes}
          month={month} today={today}
          onUpdate={onUpdateAgenda}
          onGoClient={onGoClient}
          onUpdateTask={onUpdateTask}
          onUpdatePlano={onUpdatePlano}
        />
      )}

      {/* ── Próximos dias — strip ───────────────────────────────────────────── */}
      {nextWorkDays.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 12 }}>
            Próximos dias
          </div>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6 }}>
            {nextWorkDays.map((ds) => {
              const dia    = agenda.find((d) => d.date === ds && !d.urgente);
              const client = dia ? clientMap[dia.client_id] : null;
              return (
                <DayChip key={ds} dateStr={ds} dia={dia} client={client} today={today}
                  onClick={() => client && onGoClient(client.id)} />
              );
            })}
          </div>
        </div>
      )}

      {/* ── Eventos de hoje + próximos ──────────────────────────────────────── */}
      {(todayEvents.length > 0 || upcomingEvents.length > 0) && (
        <div>
          <div className="block-head" style={{ marginTop: 24 }}><div><h2>Eventos</h2></div></div>
          <div className="klist">
            {[...todayEvents, ...upcomingEvents].map((e) => {
              const client = clients.find((c) => c.id === e.client_id);
              const isTo   = e.event_date === today;
              const dia    = new Date(e.event_date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
              return (
                <div
                  key={e.id}
                  className="klist-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedEvent(e)}
                  onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); setSelectedEvent(e); } }}
                  style={{ cursor: "pointer" }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: e.color || "var(--primary)", flex: "none" }} />
                  <span className="txt">
                    <span style={{ fontWeight: 600 }}>{e.title}</span>
                    {client && <span style={{ color: "var(--muted)" }}> · {client.name}</span>}
                  </span>
                  {isTo && <span className="pill info">hoje</span>}
                  <span className="aside">{dia}{e.event_time ? ` · ${e.event_time.slice(0, 5)}` : ""}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Modais ─────────────────────────────────────────────────────────── */}
      {showEventForm && (
        <EventModal clients={clients} onClose={() => setShowEventForm(false)}
          onSave={async (data) => { await onNew(data); setShowEventForm(false); }} />
      )}
      {selectedEvent && (
        <EventModal initial={selectedEvent} clients={clients} onClose={() => setSelectedEvent(null)}
          onSave={async (data) => { await onUpdate(selectedEvent.id, data); setSelectedEvent(null); }}
          onDelete={async () => { await onDelete(selectedEvent.id); setSelectedEvent(null); }} />
      )}
      {showUrgencia && (
        <UrgenciaModal clients={activeClients} today={today} onClose={() => setShowUrgencia(false)}
          onSave={async (data) => { await onNewAgenda(data); setShowUrgencia(false); }} />
      )}
    </div>
  );
}
